import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { mutate } from 'swr';
import axios from 'axios';
import localize from '../../../common/localize';
import useGlobalContext from '../../../hooks/useGlobalContext';
import { PopperFullWidth } from '../../../common/appStyles';
import Combobox from '../../../components/common/Combobox';
import TableComponent from '../../../components/common/TableComponent';
import ActionButtons from '../../../components/common/ActionButtons';
import DeleteConfirmationMessage from '../../../components/common/DeleteConfirmationMessage';
import NoDataLabel from '../../../components/common/NoDataLabel';
import ErrorMessage from '../../../components/common/ErrorMessage';
import { generateNoDataValues } from '../../../common/utils';

export default function ManageTeamMember({ setTeamHasChanged }) {
  const {
    teamModel,
    setTeamModel,
    TEAMS_API,
    PERSONAL_API,
    config,
    pageIndex,
    setPageIndex,
    toggleVisibleModalConfirm,
    deleteEntity,
  } = useGlobalContext();

  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [teamMember, setTeamMember] = useState(null);
  const [personalData, setPersonalData] = useState(null);

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  useEffect(() => {
    getPersonal();
  }, []);

  const getTeam = async () => {
    const data = await axios.get(`${`${TEAMS_API}/${teamModel.id}`}`, config);
    setTeamModel(data.data.Data);
  };

  const validateOperationResult = (result, isDelete) => {
    if ((!result.ok && isDelete) || (!result.Ok && !isDelete)) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([TEAMS_API, config]);
    if (isDelete) {
      if (pageIndex !== 0 && teamModel.Data.length === 1) {
        setPageIndex(pageIndex - 1);
      }
    }
    setTeamHasChanged(true);
  };

  const addNewTeamMember = async () => {
    const data = await axios.post(
      `${TEAMS_API}/${teamModel.id}/member`,
      [teamMember.id],
      config
    );

    validateOperationResult(data.data, false);
    setTeamMember(null);
    getTeam();
  };

  const handleDeleteRequest = async (personal) => {
    const deleteResult = await deleteEntity(
      `${`${TEAMS_API}/${teamModel.id}/member/${personal.id}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
    getTeam();
  };

  const handleTeamMemberChange = (value) => {
    if (!value) {
      setTeamMember(null);
    }
    setTeamMember(value);
  };

  return (
    <div style={{ width: '800px' }}>
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={`${teamMember?.Identification} | ${teamMember?.SurName} ${teamMember?.LastName}`}
          entityType={localize.team_member}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setTeamMember(null);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(teamMember);
            setShowDeleteModal(false);
            setTeamMember(null);
          }}
        />
      )}

      <form className="w-full">
        <div
          className="flex items-center justify-center lateral_padding"
          style={{ paddingRight: 18 }}
        >
          <Combobox
            id="TeamMember"
            fieldLabel={localize.team_member}
            fieldOptions={personalData?.Data ?? []}
            fieldValue={teamMember ?? null}
            customOptionLabel={(option) =>
              `${option.Identification} - ${option.SurName} ${option.LastName}`
            }
            handleValueChange={handleTeamMemberChange}
            popperFullWidth={PopperFullWidth}
          />
          <div style={{ width: 100, marginLeft: 10 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              className="app_general_button_data app_success_button btn-sm"
              onClick={() => {
                addNewTeamMember();
              }}
            >
              <span className="mr-2">{localize.add}</span>
              <Add className="text-lg" />
            </Button>
          </div>
        </div>

        <div className="my-2">
          <TableComponent
            fixedSizeInPx={300}
            noPadding
            firstColumnTitle={localize.actions}
            columnTitles={[
              localize.identification_type,
              localize.identification,
              localize.surName,
              localize.lastName,
              localize.contractor,
              localize.phone_number,
            ]}
            results={
              teamModel?.members?.length > 0
                ? teamModel?.members?.map((teamMemberObj) => ({
                    key: teamMemberObj.Id,
                    data: [
                      <ActionButtons
                        hideEditButton
                        onclickForDeleteButton={(e) => {
                          e.preventDefault();
                          setTeamMember(teamMemberObj);
                          setShowDeleteModal(true);
                          toggleVisibleModalConfirm();
                        }}
                      />,
                      teamMemberObj?.IdentificationType,
                      teamMemberObj?.Identification,
                      teamMemberObj?.SurName,
                      teamMemberObj?.LastName,
                      teamMemberObj?.Contractor?.Name ?? <NoDataLabel />,
                      teamMemberObj?.PhoneNumber ?? <NoDataLabel />,
                    ],
                  }))
                : [generateNoDataValues(7)]
            }
          />
        </div>
      </form>
    </div>
  );
}
